import { getConnection } from '@/services/integrations'
import { isTerminalStatus } from './connectionStatus'

/** How long to watch before giving up on a consent screen nobody completed. */
export const CONNECT_POLL_TIMEOUT_MS = 3 * 60_000
const POLL_INTERVAL_MS = 2_000

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Watch a connection until the provider reports a terminal state.
 *
 * Polling the provider is what establishes success. The redirect back from
 * Composio proves only that a browser visited a URL, so it is never treated as
 * evidence — a timeout here means "we do not know", not "it failed".
 *
 * @param {{ connectionId: string, workspaceId?: string, signal?: AbortSignal }} options
 * @returns {Promise<{ status: string, timedOut: boolean }>}
 */
export async function pollConnection({ connectionId, workspaceId, signal }) {
  const deadline = Date.now() + CONNECT_POLL_TIMEOUT_MS

  while (Date.now() < deadline) {
    if (signal?.aborted) return { status: 'INITIATED', timedOut: false }
    try {
      const connection = await getConnection({ connectionId, workspaceId, signal })
      if (isTerminalStatus(connection.status)) {
        return { status: connection.status, timedOut: false }
      }
    } catch (error) {
      // A transient read failure should not abandon a consent flow the user
      // may still be completing; only a hard 404 is conclusive.
      if (error?.response?.status === 404) throw error
    }
    await wait(POLL_INTERVAL_MS)
  }

  return { status: 'INITIATED', timedOut: true }
}
